import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Truck, ShoppingCart, Banknote, Plus, Pencil, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DebtRegister } from '../../components/group/DebtRegister';
import { CollectiveRevenue } from '../../components/group/CollectiveRevenue';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupOverview() {
  const { user } = useAuth();
  const perms = user?.permissions || {};
  const isAdmin = user?.role === 'admin';
  const canSeeAutoSpares = isAdmin || perms.can_view_auto_spares;
  const canManageLoans = isAdmin || perms.can_manage_loans;
  const [data, setData] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');

  // Loan CRUD
  const [showAddLoan, setShowAddLoan] = useState(false);
  const [showEditLoan, setShowEditLoan] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [newLoan, setNewLoan] = useState({ month: '', turnover: '', capital: '', initiation: '', admin: '', interest: '', agreements: '', collected: '' });

  // Overdraft
  const [showOverdraftEdit, setShowOverdraftEdit] = useState(false);
  const [overdraftInput, setOverdraftInput] = useState('');
  const [savingOverdraft, setSavingOverdraft] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const R = (amt) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amt || 0);
  const fmMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const n = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${n[parseInt(mo)] || mo} ${y}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = selectedMonth ? { month: selectedMonth } : {};
      const [overviewRes, loansRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/group-overview`, { headers, params }),
        axios.get(`${API_URL}/api/loans/months`, { headers })
      ]);
      if (overviewRes.status === 'fulfilled') {
        setData(overviewRes.value.data);
        if (!selectedMonth && overviewRes.value.data?.month) setSelectedMonth(overviewRes.value.data.month);
      } else {
        toast.error('Failed to load overview');
      }
      // Loans: prefer dedicated endpoint (for CRUD); fall back to embedded loan_months (read-only for clerks)
      if (loansRes.status === 'fulfilled') {
        setLoans(loansRes.value.data);
      } else if (overviewRes.status === 'fulfilled' && overviewRes.value.data?.loan_months) {
        setLoans(overviewRes.value.data.loan_months);
      } else {
        setLoans([]);
      }
    } finally { setLoading(false); }
  }, [selectedMonth]);

  useEffect(() => { fetchData(); }, [selectedMonth]);

  // Loan handlers
  const saveOverdraft = async () => {
    const v = parseFloat(overdraftInput);
    if (!Number.isFinite(v) || v < 0) { toast.error('Enter a valid amount'); return; }
    setSavingOverdraft(true);
    try {
      await axios.put(`${API_URL}/api/group-overview/overdraft`, { value: v }, { headers });
      toast.success('Overdraft updated');
      setShowOverdraftEdit(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingOverdraft(false); }
  };

  const handleAddLoan = async () => {
    if (!newLoan.month) { toast.error('Month required'); return; }
    try {
      await axios.post(`${API_URL}/api/loans/months`, newLoan, { headers });
      toast.success('Loan month added!');
      setNewLoan({ month: '', turnover: '', capital: '', initiation: '', admin: '', interest: '', agreements: '', collected: '' });
      setShowAddLoan(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditLoan = async () => {
    if (!editingLoan) return;
    try {
      await axios.put(`${API_URL}/api/loans/months/${editingLoan.id}`, editingLoan, { headers });
      toast.success('Updated!'); setShowEditLoan(false); setEditingLoan(null); fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/loans/months/${id}`, { headers }); toast.success('Deleted!'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const ProfitCell = ({ val, size = 'text-xl' }) => (
    <span className={`font-black ${size} ${(val || 0) >= 0 ? 'text-green-300' : 'text-red-400'}`}>{R(val)}</span>
  );

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64 text-white">Loading...</div></DashboardLayout>;

  const t = data?.transport || {};
  const a = data?.auto_spares || {};
  const l = data?.loans || {};
  const g = data?.group || {};

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 className="h-6 w-6 text-yellow-400" /> JSM Group Overview
            </h1>
            <p className="text-white text-sm">All Business Units — {data?.fy_label && `FY ${data.fy_label}`}</p>
          </div>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-48" data-testid="group-month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              {(data?.available_months || []).map(m => <SelectItem key={m} value={m}>{fmMonth(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* GROUP TOTAL BANNER */}
        <Card className={`border-2 ${(g.net_profit_after_overdraft ?? g.net_profit) >= 0 ? 'bg-green-900/40 border-green-500' : 'bg-red-900/40 border-red-500'}`}>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div><p className="text-white text-xs font-bold mb-1">GROUP REVENUE</p><p className="text-yellow-300 font-black text-xl">{R(g.revenue)}</p></div>
              <div><p className="text-white text-xs font-bold mb-1">GROUP EXPENSES</p><p className="text-orange-300 font-black text-xl">{R(g.expenses)}</p></div>
              <div><p className="text-white text-xs font-bold mb-1">OPERATING PROFIT</p><ProfitCell val={g.operating_profit} /></div>
              <div><p className="text-white text-xs font-bold mb-1">TAX (Transport only)</p><p className="text-white font-black text-xl">{R(g.tax)}</p></div>
              <div><p className="text-white text-xs font-bold mb-1">NET PROFIT AFTER TAX</p><ProfitCell val={g.net_profit} size="text-2xl" /></div>
            </div>
            {/* Group Overdraft row */}
            <div className="border-t border-white/20 pt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-center" data-testid="group-overdraft-section">
              <button
                type="button"
                onClick={() => { setOverdraftInput(String(g.overdraft_used ?? 220000)); setShowOverdraftEdit(true); }}
                className="flex items-center justify-center gap-2 text-amber-300 hover:text-amber-200 text-sm font-bold"
                data-testid="edit-group-overdraft-btn"
              >
                <CreditCard className="h-4 w-4" /> GROUP OVERDRAFT USED
                <Pencil className="h-3 w-3 opacity-70" />
              </button>
              <div className="text-center">
                <p className="text-amber-300 text-xs font-bold mb-1">Less: Overdraft</p>
                <p className="text-amber-300 font-black text-xl" data-testid="group-overdraft-value">{R(g.overdraft_used || 0)}</p>
              </div>
              <div className="text-center">
                <p className="text-white text-xs font-bold mb-1">OPERATING PROFIT (after overdraft)</p>
                <ProfitCell val={g.operating_profit_after_overdraft ?? (g.operating_profit - (g.overdraft_used || 0))} size="text-2xl" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3 BUSINESS UNITS */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* TRANSPORT */}
          <Card className="bg-slate-800 border-slate-600">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base"><Truck className="h-5 w-5 text-blue-400" /> JSM Transport</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-1"><span className="text-white text-sm">Revenue</span><span className="text-yellow-300 font-bold">{R(t.revenue)}</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Expenses</span><span className="text-orange-300 font-bold">{R(t.expenses)}</span></div>
              <div className="flex justify-between py-1 border-t border-slate-600 pt-2"><span className="text-white font-bold">Operating Profit</span><ProfitCell val={t.operating_profit} size="text-lg" /></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Tax (27%)</span><span className="text-white font-bold">{R(t.tax)}</span></div>
              <div className={`flex justify-between py-2 px-2 rounded ${t.net_profit >= 0 ? 'bg-green-900/40 border border-green-600' : 'bg-red-900/40 border border-red-600'}`}>
                <span className="text-white font-black">NET PROFIT</span>
                <ProfitCell val={t.net_profit} size="text-lg" />
              </div>
              <CollectiveRevenue transport={t} headers={headers} onChanged={fetchData} />
            </CardContent>
          </Card>

          {/* AUTO SPARES - only visible if user has permission */}
          {canSeeAutoSpares && (
          <Card className="bg-slate-800 border-slate-600">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base"><ShoppingCart className="h-5 w-5 text-emerald-400" /> JSM Auto Spares</CardTitle>
              {a.period_label && <p className="text-xs text-slate-400 mt-1">{a.period_label} (linked to Auto Spares Dashboard)</p>}
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-1"><span className="text-white text-sm">Month Sales</span><span className="text-yellow-300 font-bold">{R(a.revenue)}</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Month Expenses</span><span className="text-orange-300 font-bold">{R(a.expenses)}</span></div>
              {(a.salaries > 0 || a.commissions > 0) && (
                <div className="flex justify-between py-1 text-xs"><span className="text-slate-400">+ Salaries & Commission</span><span className="text-slate-400">{R((a.salaries || 0) + (a.commissions || 0))}</span></div>
              )}
              <div className={`flex justify-between py-2 px-2 rounded mt-2 border-t border-slate-600 pt-3 ${a.cash_on_hand >= 0 ? 'bg-green-900/40 border border-green-600' : 'bg-red-900/40 border border-red-600'}`}>
                <span className="text-white font-black">CASH ON HAND</span>
                <ProfitCell val={a.cash_on_hand} size="text-lg" />
              </div>
              <p className="text-xs text-slate-400 italic pt-1">Tax not applied at this level</p>
            </CardContent>
          </Card>
          )}

          {/* 2K2DAY LOANS */}
          <Card className="bg-slate-800 border-slate-600">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-base"><Banknote className="h-5 w-5 text-purple-400" /> 2K2Day Loans</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-1"><span className="text-white text-sm">Turnover</span><span className="text-yellow-300 font-bold">{R(l.turnover)}</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Capital Lent</span><span className="text-orange-300 font-bold">{R(l.capital)}</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Initiation + Admin + Interest</span><span className="text-cyan-300 font-bold">{R((l.initiation || 0) + (l.admin || 0) + (l.interest || 0))}</span></div>
              <div className="flex justify-between py-1 border-t border-slate-600 pt-2"><span className="text-white font-bold">Profit (Turnover - Capital)</span><ProfitCell val={l.profit} size="text-lg" /></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Collected</span><span className="text-green-300 font-bold">{R(l.collected)}</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Collection %</span><span className={`font-bold ${l.collection_pct >= 80 ? 'text-green-300' : l.collection_pct >= 50 ? 'text-yellow-300' : 'text-red-400'}`}>{l.collection_pct || 0}%</span></div>
              <div className="flex justify-between py-1"><span className="text-white text-sm">Agreements</span><span className="text-white font-bold">{l.agreements || 0}</span></div>
            </CardContent>
          </Card>
        </div>

        {isAdmin && <DebtRegister headers={headers} />}

        {/* 2K2DAY LOANS TABLE */}
        <Card className="bg-slate-800 border-slate-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base"><Banknote className="h-5 w-5 text-purple-400" /> 2K2Day Loans — Monthly Breakdown</CardTitle>
            {canManageLoans && (
            <Dialog open={showAddLoan} onOpenChange={setShowAddLoan}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-loan-month-btn"><Plus className="h-3 w-3 mr-1" /> Add Month</Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
                <DialogHeader><DialogTitle>ADD LOAN MONTH</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Month (YYYY-MM) *</Label><Input value={newLoan.month} onChange={e => setNewLoan({...newLoan, month: e.target.value})} placeholder="e.g., 2026-05" className="bg-slate-700 border-slate-600" data-testid="loan-month-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Turnover</Label><Input type="number" step="0.01" value={newLoan.turnover} onChange={e => setNewLoan({...newLoan, turnover: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                    <div><Label>Capital</Label><Input type="number" step="0.01" value={newLoan.capital} onChange={e => setNewLoan({...newLoan, capital: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Initiation</Label><Input type="number" step="0.01" value={newLoan.initiation} onChange={e => setNewLoan({...newLoan, initiation: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                    <div><Label>Admin</Label><Input type="number" step="0.01" value={newLoan.admin} onChange={e => setNewLoan({...newLoan, admin: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                    <div><Label>Interest</Label><Input type="number" step="0.01" value={newLoan.interest} onChange={e => setNewLoan({...newLoan, interest: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Agreements</Label><Input type="number" value={newLoan.agreements} onChange={e => setNewLoan({...newLoan, agreements: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                    <div><Label>Collected</Label><Input type="number" step="0.01" value={newLoan.collected} onChange={e => setNewLoan({...newLoan, collected: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  </div>
                  <Button onClick={handleAddLoan} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-loan-btn">SAVE</Button>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-white font-bold">Month</TableHead>
                    <TableHead className="text-white font-bold text-right">Turnover</TableHead>
                    <TableHead className="text-white font-bold text-right">Capital</TableHead>
                    <TableHead className="text-white font-bold text-right">Initiation</TableHead>
                    <TableHead className="text-white font-bold text-right">Admin</TableHead>
                    <TableHead className="text-white font-bold text-right">Interest</TableHead>
                    <TableHead className="text-white font-bold text-right">Agreements</TableHead>
                    <TableHead className="text-white font-bold text-right">Collected</TableHead>
                    <TableHead className="text-white font-bold text-right">Collection%</TableHead>
                    <TableHead className="text-white font-bold text-right">Profit</TableHead>
                    <TableHead className="text-white font-bold text-right w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map(ln => {
                    const profit = (ln.turnover || 0) - (ln.capital || 0);
                    const collPct = ln.turnover > 0 ? ((ln.collected || 0) / ln.turnover * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow key={ln.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{fmMonth(ln.month)}</TableCell>
                        <TableCell className="text-yellow-300 text-right font-bold">{R(ln.turnover)}</TableCell>
                        <TableCell className="text-orange-300 text-right">{R(ln.capital)}</TableCell>
                        <TableCell className="text-white text-right">{R(ln.initiation)}</TableCell>
                        <TableCell className="text-white text-right">{R(ln.admin)}</TableCell>
                        <TableCell className="text-white text-right">{R(ln.interest)}</TableCell>
                        <TableCell className="text-white text-right">{ln.agreements}</TableCell>
                        <TableCell className="text-green-300 text-right font-bold">{R(ln.collected)}</TableCell>
                        <TableCell className={`text-right font-bold ${parseFloat(collPct) >= 80 ? 'text-green-300' : parseFloat(collPct) >= 50 ? 'text-yellow-300' : 'text-red-400'}`}>{collPct}%</TableCell>
                        <TableCell className={`text-right font-bold ${profit >= 0 ? 'text-green-300' : 'text-red-400'}`}>{R(profit)}</TableCell>
                        <TableCell className="text-right">
                          {canManageLoans ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-6 w-6 p-0" onClick={() => { setEditingLoan({...ln}); setShowEditLoan(true); }}><Pencil className="h-3 w-3" /></Button>
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-6 w-6 p-0" onClick={() => handleDeleteLoan(ln.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                          ) : <span className="text-slate-500 text-xs">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {loans.length > 0 && (
                    <TableRow className="border-slate-700 bg-slate-700/50">
                      <TableCell className="text-white font-black">TOTAL</TableCell>
                      <TableCell className="text-yellow-300 text-right font-black">{R(loans.reduce((s, l) => s + (l.turnover || 0), 0))}</TableCell>
                      <TableCell className="text-orange-300 text-right font-bold">{R(loans.reduce((s, l) => s + (l.capital || 0), 0))}</TableCell>
                      <TableCell className="text-white text-right font-bold">{R(loans.reduce((s, l) => s + (l.initiation || 0), 0))}</TableCell>
                      <TableCell className="text-white text-right font-bold">{R(loans.reduce((s, l) => s + (l.admin || 0), 0))}</TableCell>
                      <TableCell className="text-white text-right font-bold">{R(loans.reduce((s, l) => s + (l.interest || 0), 0))}</TableCell>
                      <TableCell className="text-white text-right font-bold">{loans.reduce((s, l) => s + (l.agreements || 0), 0)}</TableCell>
                      <TableCell className="text-green-300 text-right font-black">{R(loans.reduce((s, l) => s + (l.collected || 0), 0))}</TableCell>
                      <TableCell />
                      <TableCell className="text-green-300 text-right font-black">{R(loans.reduce((s, l) => s + (l.turnover || 0) - (l.capital || 0), 0))}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit Loan Dialog */}
        <Dialog open={showEditLoan} onOpenChange={setShowEditLoan}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
            <DialogHeader><DialogTitle>EDIT LOAN MONTH — {editingLoan?.month && fmMonth(editingLoan.month)}</DialogTitle></DialogHeader>
            {editingLoan && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Turnover</Label><Input type="number" step="0.01" value={editingLoan.turnover} onChange={e => setEditingLoan({...editingLoan, turnover: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Capital</Label><Input type="number" step="0.01" value={editingLoan.capital} onChange={e => setEditingLoan({...editingLoan, capital: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Initiation</Label><Input type="number" step="0.01" value={editingLoan.initiation} onChange={e => setEditingLoan({...editingLoan, initiation: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Admin</Label><Input type="number" step="0.01" value={editingLoan.admin} onChange={e => setEditingLoan({...editingLoan, admin: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Interest</Label><Input type="number" step="0.01" value={editingLoan.interest} onChange={e => setEditingLoan({...editingLoan, interest: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Agreements</Label><Input type="number" value={editingLoan.agreements} onChange={e => setEditingLoan({...editingLoan, agreements: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  <div><Label>Collected</Label><Input type="number" step="0.01" value={editingLoan.collected} onChange={e => setEditingLoan({...editingLoan, collected: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                </div>
                <Button onClick={handleEditLoan} className="w-full bg-purple-600 hover:bg-purple-700">UPDATE</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Overdraft edit dialog */}
        <Dialog open={showOverdraftEdit} onOpenChange={setShowOverdraftEdit}>
          <DialogContent className="bg-slate-800 border-slate-600">
            <DialogHeader>
              <DialogTitle className="text-white">Overdraft Used</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-slate-300">Total balance currently drawn from your overdraft. Deducted from Cash on Hand to show what is truly available right now.</p>
              <div>
                <Label className="text-white">Amount (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={overdraftInput}
                  onChange={(e) => setOverdraftInput(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white text-lg"
                  data-testid="overdraft-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowOverdraftEdit(false)} className="flex-1">Cancel</Button>
                <Button onClick={saveOverdraft} disabled={savingOverdraft} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="save-overdraft-btn">
                  {savingOverdraft ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
